import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PublicNavbar from '@/components/layout/PublicNavbar';
import PublicFooter from '@/components/layout/PublicFooter';
import { ArrowLeft, Clock } from 'lucide-react';
import { articlesData, featuredArticle } from '@/data/articles';

export default function ArticleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  let article = null;
  if (id === 'featured') {
    article = featuredArticle;
  } else {
    article = articlesData.find(a => a.id.toString() === id);
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FFFDF9] flex flex-col">
        <PublicNavbar />
        <div className="flex-grow flex flex-col items-center justify-center text-center p-6">
          <h2 className="text-3xl font-bold text-[#302621] mb-4">Artikel Tidak Ditemukan</h2>
          <p className="text-[#7A695B] mb-8">Maaf, artikel yang Anda cari tidak tersedia atau telah dihapus.</p>
          <button onClick={() => navigate('/artikel')} className="bg-[#D4AF37] hover:bg-[#B58B22] text-white px-6 py-3 rounded-full font-semibold transition-colors">
            Kembali ke Artikel
          </button>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF9] text-[#4A3B32] font-sans selection:bg-[#D4AF37] selection:text-white">
      <PublicNavbar />

      <main className="pt-24 pb-16">
        <article className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <Link to="/artikel" className="inline-flex items-center text-[#D4AF37] hover:text-[#B58B22] font-semibold mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Artikel
          </Link>

          <header className="mb-10 text-center">
            <span className="inline-block py-1 px-3 bg-[#E6D5B8]/40 text-[#8C6208] text-xs font-bold tracking-widest uppercase rounded mb-4">
              {article.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-[#302621] mb-6 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center justify-center text-sm text-[#7A695B] gap-4">
              <span className="font-semibold text-[#302621]">{article.author}</span>
              <div className="w-1.5 h-1.5 rounded-full bg-[#E6D5B8]"></div>
              <span>{article.date}</span>
              <div className="w-1.5 h-1.5 rounded-full bg-[#E6D5B8]"></div>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {article.readTime}</span>
            </div>
          </header>

          <div className="w-full aspect-[16/9] md:aspect-[21/9] bg-gray-100 rounded-3xl mb-12 overflow-hidden shadow-sm">
            <img 
              src={article.img} 
              alt={article.title} 
              className="w-full h-full object-cover" 
              onError={(e) => e.target.src = "https://images.unsplash.com/photo-1596462502278-27bfdc403348?q=80&w=1200&auto=format&fit=crop"}
            />
          </div>

          <div className="prose prose-lg prose-stone max-w-none prose-headings:text-[#302621] prose-a:text-[#D4AF37]">
            {article.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-6 text-[#4A3B32] leading-relaxed text-lg">
                {paragraph}
              </p>
            ))}
          </div>
          
          <div className="mt-16 pt-8 border-t border-[#E6D5B8]/40 flex flex-col sm:flex-row items-center justify-between gap-4">
             <h3 className="text-xl font-bold text-[#302621]">Bagikan Artikel Ini</h3>
             <div className="flex gap-4">
                <button className="w-10 h-10 rounded-full bg-[#F9F5EC] text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" /></svg>
                </button>
                <button className="w-10 h-10 rounded-full bg-[#F9F5EC] text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" /></svg>
                </button>
             </div>
          </div>
        </article>
      </main>

      <PublicFooter />
    </div>
  );
}
